import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// Wire contracts for the advisory pre-auth SAML lookup (ADR-101 Rule 1
// amendment): `POST /api/v1/eligibility/saml/sessions`.

/** Upper bound on a sealed SAML session handle (KMS-encrypted data key + AES-GCM payload). */
export const SAML_SESSION_HANDLE_MAX_LENGTH = 4096;

/** Which sealed roster contact the member chose to receive their OTP at. */
export const samlContactSchema = z.enum(['email', 'phone']);
export type SamlContact = z.infer<typeof samlContactSchema>;

/** The request carries the saml-temp id_token as a bearer; the body is empty. */
export const samlSessionCreateRequestSchema = z.object({}).strict();
export type SamlSessionCreateRequest = z.infer<typeof samlSessionCreateRequestSchema>;

const maskedContactSchema = z.object({ masked: z.string().min(1) });

/**
 * The employer the verified SSO identity belongs to, as the client and
 * sub-client ids a signup link carries. Optional (ADR-135): an older server
 * sends neither, and a client that does not need them ignores them.
 */
const samlPartnerRefsShape = {
  clientRef: z.string().min(1).optional(),
  subClientRef: z.string().min(1).optional(),
};

/**
 * Client-parsed response (ADR-135): deliberately not `.strict()`, so a new
 * key never breaks an older bundle. A match carries ONLY masked contacts and
 * the opaque sealed session; a no-match carries neither.
 */
export const samlSessionViewSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('matched'),
    session: z.string().min(1),
    contacts: z.object({
      email: maskedContactSchema.optional(),
      phone: maskedContactSchema.optional(),
    }),
    /**
     * Whether the roster row carries a date of birth. The value itself stays
     * sealed; the client only learns whether it must collect one. Optional
     * for forward compatibility (ADR-135); absent reads as "collect one".
     */
    hasRosterDob: z.boolean().optional(),
    ...samlPartnerRefsShape,
  }),
  z.object({ status: z.literal('no_match'), ...samlPartnerRefsShape }),
]);
export type SamlSessionView = z.infer<typeof samlSessionViewSchema>;
export const samlSessionViewEnvelopeSchema = apiEnvelope(samlSessionViewSchema);

const E164_PHONE = /^\+[1-9]\d{6,14}$/;
const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A roster email in the form OTP delivery and the members row use (trimmed,
 * lowercased), or null when it is not an address an OTP can reach.
 */
export function normalizeRosterEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  return EMAIL_ADDRESS.test(normalized) ? normalized : null;
}

/**
 * Roster phones arrive in whatever shape the employer file used. OTP delivery
 * needs E.164: a bare ten-digit US number gains `+1`, an eleven-digit number
 * leading with 1 gains `+`, and a `+`-prefixed number keeps its digits. Any
 * other shape is undeliverable and yields null, so it is never offered.
 */
export function normalizeRosterPhone(phone: string): string | null {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  let candidate: string;
  if (trimmed.startsWith('+')) candidate = `+${digits}`;
  else if (digits.length === 10) candidate = `+1${digits}`;
  else if (digits.length === 11 && digits.startsWith('1')) candidate = `+${digits}`;
  else return null;
  return E164_PHONE.test(candidate) ? candidate : null;
}
